import { useCallback, useEffect, useRef, useState } from 'react'
import API from '../../../../lib/commons/api'
import Auth from '../../../../lib/commons/auth'
import {
  aiAgentConverse,
  AiAgentImageRef,
  AiAgentWbsItemRef,
  PageContext,
} from '../../../../lib/functions/ai'
import {
  MessageSegment,
  TokenStreamedPayload,
  combineTokenChunks,
  toTextSegments,
} from '../tokenChunks'

export type AiAgentChatMessage = {
  // user / assistant のメッセージ。assistant はトークンを追記して成長する。
  role: 'user' | 'assistant'
  // 本文。assistant の応答はツール呼び出しをはさむと複数のセグメントに分かれる。
  // user 入力・エラー文言は常に単一のテキストセグメント。
  segments: MessageSegment[]
  isError?: boolean
  // ストリーミング完了済みかどうか。
  complete?: boolean
  // ユーザーがメッセージと共に添付した画像（表示用サムネイル）。assistant では未使用。
  images?: { url: string }[]
  // ユーザーがメッセージと共に添付したWBSアイテム参照。assistant では未使用。
  // 何を渡したかがユーザーから見えるよう、送信済みバブルにも表示する。
  wbsItemAttachments?: AiAgentWbsItemRef[]
}

// STOMP の通知ペイロード（トークンは tokenChunks.ts の TokenStreamedPayload）。
type TurnCompletedPayload = { conversationUuid: string; stopReason: string }
type AgentErrorPayload = {
  conversationUuid: string
  code: string
  message: string
}

// 同一会話でストリーミング中のターンが存在する場合、converse は 409 を返す。
const isConversationBusyError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === 409

/**
 * 直近のターンがどう終わったか。送信待ち行列の自動送信（completed）と破棄（error）を
 * 呼び出し側で出し分けるために公開する。同じ結果に二重反応しないよう seq を持つ。
 */
export type AiAgentTurnResult = {
  seq: number
  status: 'completed' | 'error'
}

/**
 * sendMessage の受付結果。busy は同一会話でターンが進行中のため受け付けられなかったことを表し、
 * 呼び出し側は送信内容を送信待ち行列へ戻すために使う。
 */
export type AiAgentSendResult = 'accepted' | 'busy' | 'failed'

export type UseAiAgentConversationInput = {
  greetingText: string
  // 同一会話でストリーミング中に再送信した場合（409）の通知文言。
  busyErrorText: string
  // converse 呼び出し自体が失敗した場合の文言。
  generalErrorText: string
  // ストリーミング中にエージェント側でエラーが発生した場合の既定文言。
  streamErrorText: string
}

/**
 * AIエージェントとのマルチターン会話（converse 202 + STOMPストリーミング）を管理するフック。
 * フローティングパネル・専用ページの双方から利用するため、UI文言は呼び出し側から注入する。
 */
export const useAiAgentConversation = ({
  greetingText,
  busyErrorText,
  generalErrorText,
  streamErrorText,
}: UseAiAgentConversationInput) => {
  const greeting: AiAgentChatMessage = {
    role: 'assistant',
    segments: toTextSegments(greetingText),
    complete: true,
  }
  const [messages, setMessages] = useState<AiAgentChatMessage[]>([greeting])
  const [isStreaming, setIsStreaming] = useState(false)
  // isStreaming の state はレンダーを挟むまで更新が見えないため、送信可否の判定は ref で行う。
  // 応答完了の直後に送信した場合など、同じレンダー内で連続して送信されるのを防ぐ。
  const isStreamingRef = useRef(false)
  const [inlineError, setInlineError] = useState<string | undefined>(undefined)
  const [lastTurnResult, setLastTurnResult] = useState<
    AiAgentTurnResult | undefined
  >(undefined)
  const turnSeqRef = useRef(0)

  // 会話をまたいで安定させる conversationUuid。初回ターンは null（未指定）で送信し、
  // 202 で返ってきた値を保持して以降のターンで再利用する（マルチターン）。
  const conversationUuidRef = useRef<string | undefined>(undefined)
  // 受信したトークンを index 順にバッファリングし、結合してアシスタント末尾メッセージに反映する。
  // バッファはターン開始時にリセットする（index はターンごとに 0 から振り直される）。
  const tokenBufferRef = useRef<TokenStreamedPayload[]>([])
  // 現在 STOMP 購読済みの conversationUuid。同一会話の 2 回目以降ターンで再購読しないためのガード。
  const subscribedConversationUuidRef = useRef<string | undefined>(undefined)
  // アンマウント時に購読を解除するためにトピック一覧を保持する。
  const subscribedTopicsRef = useRef<string[]>([])

  const unsubscribeActive = useCallback(() => {
    subscribedTopicsRef.current.forEach(topic =>
      API.notification.unsubscribe(topic)
    )
    subscribedTopicsRef.current = []
    subscribedConversationUuidRef.current = undefined
  }, [])

  // バッファされたトークンを index 順に組み立て、末尾アシスタントメッセージへ反映する。
  const flushAssistantText = useCallback(() => {
    const segments = combineTokenChunks(tokenBufferRef.current)
    setMessages(prev => {
      const next = [...prev]
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant' && !next[i].complete) {
          next[i] = { ...next[i], segments }
          break
        }
      }
      return next
    })
  }, [])

  const markAssistantComplete = useCallback((errorText?: string) => {
    setMessages(prev => {
      const next = [...prev]
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant' && !next[i].complete) {
          next[i] = {
            ...next[i],
            complete: true,
            ...(errorText !== undefined
              ? { segments: toTextSegments(errorText), isError: true }
              : {}),
          }
          break
        }
      }
      return next
    })
  }, [])

  const beginTurn = useCallback(() => {
    isStreamingRef.current = true
    setIsStreaming(true)
  }, [])

  const endTurn = useCallback((status: AiAgentTurnResult['status']) => {
    isStreamingRef.current = false
    setIsStreaming(false)
    turnSeqRef.current += 1
    setLastTurnResult({ seq: turnSeqRef.current, status })
    // ターン完了時は購読を維持する（同一 conversationUuid でマルチターンを継続するため）。
    // 購読解除はリセット時またはアンマウント時に行う。
  }, [])

  // 202 で確定した conversationUuid に対してストリーミングトピックを購読する。
  // 同一 conversationUuid に対しては一度だけ購読し、マルチターン全体で購読を維持する。
  // 既知の制限: バックエンドはこの購読より前にトークンを publish しうる（理論上の取りこぼし）。
  // バックエンドの最初の flush は invoke 開始から 150ms 以上、加えてエージェントのコールドスタートが
  // あるため、202 受信直後に購読する本実装で MVP としては許容できるレースである。
  const subscribeToConversation = useCallback(
    (conversationUuid: string) => {
      // 既に同一会話の購読が存在する場合は再購読しない（重複購読によるトークン多重配信を防ぐ）。
      if (subscribedConversationUuidRef.current === conversationUuid) {
        return
      }
      const tenant = Auth.getCurrentTenant()
      if (!tenant) {
        return
      }
      const tenantUuid = tenant.tenantUuid
      const tokenTopic = `${tenantUuid}/AiAgentTokenStreamed/${conversationUuid}`
      const completedTopic = `${tenantUuid}/AiAgentTurnCompleted/${conversationUuid}`
      const errorTopic = `${tenantUuid}/AiAgentError/${conversationUuid}`

      API.notification.subscribe<TokenStreamedPayload>(tokenTopic, payload => {
        if (conversationUuidRef.current !== conversationUuid) {
          return
        }
        tokenBufferRef.current.push(payload)
        flushAssistantText()
      })
      API.notification.subscribe<TurnCompletedPayload>(
        completedTopic,
        payload => {
          if (payload.conversationUuid !== conversationUuid) {
            return
          }
          markAssistantComplete()
          endTurn('completed')
        }
      )
      API.notification.subscribe<AgentErrorPayload>(errorTopic, payload => {
        if (payload.conversationUuid !== conversationUuid) {
          return
        }
        markAssistantComplete(payload.message || streamErrorText)
        endTurn('error')
      })

      subscribedConversationUuidRef.current = conversationUuid
      subscribedTopicsRef.current = [tokenTopic, completedTopic, errorTopic]
    },
    [flushAssistantText, markAssistantComplete, endTurn, streamErrorText]
  )

  const sendMessage = useCallback(
    // pageContext は送信時点の画面状態を反映する必要があるため（設計文書のとおりターンごとに
    // 変わりうる）、フックの入力ではなく sendMessage の呼び出し時引数として受け取る。
    async (
      text: string,
      pageContext?: PageContext,
      images?: AiAgentImageRef[],
      wbsItemAttachments?: AiAgentWbsItemRef[]
    ): Promise<AiAgentSendResult> => {
      if (isStreamingRef.current) {
        return 'busy'
      }
      const messageText = text.trim()
      if (!messageText) {
        return 'failed'
      }

      setInlineError(undefined)
      beginTurn()
      // 新しいターン用にトークンバッファをリセットし、ユーザー発言と空のアシスタント枠を追加する。
      // 409 で受け付けられなかった場合は進行中ターンのトークンを取りこぼすため、元の内容を控えておく。
      const tokenBufferBeforeTurn = tokenBufferRef.current
      tokenBufferRef.current = []
      setMessages(prev => [
        ...prev,
        {
          role: 'user',
          segments: toTextSegments(messageText),
          ...(images && images.length > 0
            ? { images: images.map(image => ({ url: image.url })) }
            : {}),
          ...(wbsItemAttachments && wbsItemAttachments.length > 0
            ? { wbsItemAttachments }
            : {}),
        },
        { role: 'assistant', segments: [] },
      ])

      try {
        const response = await aiAgentConverse({
          prompt: messageText,
          conversationUuid: conversationUuidRef.current,
          pageContext,
          images,
          wbsItemAttachments,
        })
        conversationUuidRef.current = response.conversationUuid
        // 202 受信直後に購読する（トークンの取りこぼしを最小化）。
        subscribeToConversation(response.conversationUuid)
        return 'accepted'
      } catch (error) {
        if (isConversationBusyError(error)) {
          setInlineError(busyErrorText)
          // 409 は「サーバ側でターンが進行中」なので、このターンは無かったことにして
          // 楽観的に追加した発言と空のアシスタント枠、トークンバッファを元に戻す。
          tokenBufferRef.current = tokenBufferBeforeTurn
          setMessages(prev => {
            const last = prev[prev.length - 1]
            const secondLast = prev[prev.length - 2]
            if (last?.role === 'assistant' && secondLast?.role === 'user') {
              return prev.slice(0, -2)
            }
            return prev
          })
          // 進行中のターンは続いているため endTurn せず応答中のままにする。
          // 送信待ち行列はそのターンの完了通知で自動送信される。
          return 'busy'
        }
        markAssistantComplete(generalErrorText)
        endTurn('error')
        return 'failed'
      }
    },
    [
      beginTurn,
      subscribeToConversation,
      markAssistantComplete,
      endTurn,
      busyErrorText,
      generalErrorText,
    ]
  )

  /**
   * 会話を初期状態へ戻す。応答中でも実行でき、その場合は購読を解除して進行中のターンから
   * 切り離す（サーバ側のターンは続くが、以降のトークンも完了通知も受け取らない）。
   */
  const resetConversation = useCallback(() => {
    unsubscribeActive()
    isStreamingRef.current = false
    setIsStreaming(false)
    conversationUuidRef.current = undefined
    tokenBufferRef.current = []
    setMessages([
      {
        role: 'assistant',
        segments: toTextSegments(greetingText),
        complete: true,
      },
    ])
    setInlineError(undefined)
    setLastTurnResult(undefined)
  }, [unsubscribeActive, greetingText])

  // アンマウント時に購読を解除する。
  useEffect(() => {
    return () => {
      unsubscribeActive()
    }
  }, [unsubscribeActive])

  return {
    messages,
    isStreaming,
    inlineError,
    lastTurnResult,
    sendMessage,
    resetConversation,
  }
}
